import { cloneDeep } from 'lodash-es';
import { PlayerOnlineItem } from '@/models/playerOnline';
import { createLoggedPlayersPageHeaders } from '@/pages/system/loggedPlayers/LoggedPlayersPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { playerOnlineService } from '@/services/playerOnlineService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { IPageOptions } from '@/types';
import type { ILoggedPlayersStats, IPlayerOnlineFilters } from '@/types/playerOnline';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'connectionId', order: 'desc' as const }];

function createPlayerOnlineStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<PlayerOnlineItem[]>([]);
    const total = ref<number>(0);
    const stats = ref<ILoggedPlayersStats | undefined>(undefined);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IPlayerOnlineFilters>({
        loggedPlayersSearch: {
            value: null,
            title: 'loggedPlayers.search',
            allowSaveState: true,
        },
        loggedPlayersPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100000,
        page: 1,
        rowsPerPageItems: [100000],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.loggedPlayersSearch.value = null;
        filters.loggedPlayersPlatformId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createLoggedPlayersPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const reloadFiltersFromStorage = () => {
        resetFilters();
    };

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);

            const params = {
                filters: filtersToSend as IPlayerOnlineFilters,
                options: {
                    sortBy: [],
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
            };

            const response = await playerOnlineService.list(params);

            const rows = response.data.rows ?? [];
            items.value = rows.map((row) => new PlayerOnlineItem(row));
            total.value = response.data.total ?? rows.length;
            stats.value = { total_users: total.value };
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        stats.value = undefined;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        stats,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        hasRefresh: true,
    };
}

export function usePlayerOnlineStore(storeId: string = 'loggedPlayers') {
    const useStore = defineStore(storeId, () => createPlayerOnlineStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'loggedPlayers', defaultSortBy),
    });
    return useStore();
}
